import { create } from 'zustand';
import {
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import type {
  Flow,
  FlowNode,
  FlowEdge,
  NodeKind,
  HumanOversightMode,
} from '../types';
import { uid } from '../lib/utils';
import { defaultNodeData } from '../lib/factories';
import {
  computeLoopBody,
  EST_NODE_W,
  EST_NODE_H,
  LOOP_PAD_X,
  LOOP_PAD_TOP,
  LOOP_PAD_BOTTOM,
} from '../lib/loopLayout';

// Dados que cada nó do canvas carrega. `config` é a parte editável no painel de propriedades.
export interface StudioNodeData extends Record<string, unknown> {
  kind: NodeKind;
  config: Record<string, unknown>;
  oversightOverride?: HumanOversightMode;
}

export type StudioNode = Node<StudioNodeData>;
export interface StudioEdgeData extends Record<string, unknown> {
  isLoopBack?: boolean;
}
export type StudioEdge = Edge<StudioEdgeData>;

export interface NodeRunState {
  status: 'pending' | 'running' | 'done' | 'error' | 'awaiting_human';
  iteration?: number;
}

interface FlowStoreState {
  flowId: string | null;
  meta: Omit<Flow, 'nodes' | 'edges'> | null;
  nodes: StudioNode[];
  edges: StudioEdge[];
  selectedNodeId: string | null;
  dirty: boolean;
  runStatus: Record<string, NodeRunState>;
  pendingDeleteIds: string[]; // nós aguardando confirmação de exclusão

  loadFlow: (flow: Flow) => void;
  reset: () => void;

  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (conn: Connection) => void;
  deleteEdge: (id: string) => void;

  addNode: (kind: NodeKind, position: { x: number; y: number }, parentId?: string) => void;
  reparentNode: (childId: string, newParentId: string | null) => void;
  updateNodeConfig: (id: string, patch: Record<string, unknown>) => void;
  setLoopSize: (id: string, width: number, height: number) => void;
  setNodeOversight: (id: string, mode: HumanOversightMode | undefined) => void;
  requestDeleteNodes: (ids: string[]) => void; // abre confirmação
  cancelDelete: () => void;
  deleteNode: (id: string) => void; // exclusão efetiva (após confirmar)
  duplicateNode: (id: string) => void;
  setSelected: (id: string | null) => void;

  updateMeta: (patch: Partial<Omit<Flow, 'nodes' | 'edges'>>) => void;
  setOversightMode: (mode: HumanOversightMode) => void;

  toFlowDTO: () => Flow;
  markSaved: () => void;

  setNodeRunStatus: (nodeId: string, state: NodeRunState) => void;
  resetRunStatus: () => void;
}

// --- Conversão DTO <-> React Flow -------------------------------------------

function toStudioNode(n: FlowNode): StudioNode {
  return {
    id: n.id,
    type: 'studio',
    position: n.position,
    data: { kind: n.kind, config: { ...n.data }, oversightOverride: n.oversightOverride },
  };
}

function toStudioEdge(e: FlowEdge): StudioEdge {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    type: e.isLoopBack ? 'loop' : 'animated',
    label: e.label,
    data: { isLoopBack: e.isLoopBack },
  };
}

function fromStudioNode(n: StudioNode): FlowNode {
  return {
    id: n.id,
    kind: n.data.kind,
    position: n.position,
    data: n.data.config,
    parentId: n.parentId,
    oversightOverride: n.data.oversightOverride,
  };
}

// Garante que nós-pai (loopGroup) apareçam antes dos filhos (exigência do React Flow).
function parentsFirst(nodes: StudioNode[]): StudioNode[] {
  return [...nodes].sort((a, b) => {
    const ap = a.type === 'loopGroup' ? 0 : 1;
    const bp = b.type === 'loopGroup' ? 0 : 1;
    return ap - bp;
  });
}

function fromStudioEdge(e: StudioEdge): FlowEdge {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? undefined,
    targetHandle: e.targetHandle ?? undefined,
    isLoopBack: e.data?.isLoopBack,
    label: typeof e.label === 'string' ? e.label : undefined,
  };
}

// Monta o layout de containers de loop: o nó Loop vira um group que envolve seus filhos.
// Retorna os nós do React Flow (parents primeiro) e o conjunto de ids "dentro de loop"
// (corpo + loops com corpo) para elevar o z-index das arestas internas.
function buildLoopLayout(dtoNodes: FlowNode[], dtoEdges: FlowEdge[]): {
  nodes: StudioNode[];
  insideIds: Set<string>;
  loopBodies: Map<string, Set<string>>;
} {
  const studio = dtoNodes.map(toStudioNode);
  const byId = new Map(studio.map((n) => [n.id, n]));
  const insideIds = new Set<string>();
  const loopBodies = new Map<string, Set<string>>();

  // Tamanho padrão de um container de loop vazio (recém-criado).
  const DEFAULT_LOOP_W = 380;
  const DEFAULT_LOOP_H = 240;

  dtoNodes
    .filter((n) => n.kind === 'loop')
    .forEach((loop) => {
      const loopNode = byId.get(loop.id)!;
      // Loop é SEMPRE um container (mesmo vazio) — fica igual ao de um template ao arrastar.
      loopNode.type = 'loopGroup';
      loopNode.zIndex = 0;
      insideIds.add(loop.id);

      const body = computeLoopBody(dtoNodes, dtoEdges, loop.id);
      loopBodies.set(loop.id, body);
      const children = [...body].map((cid) => byId.get(cid)).filter(Boolean) as StudioNode[];
      const cfg = loop.data as { width?: number; height?: number };

      if (!children.length) {
        // Container vazio: usa tamanho manual ou padrão, mantém a própria posição.
        const width = typeof cfg.width === 'number' ? cfg.width : DEFAULT_LOOP_W;
        const height = typeof cfg.height === 'number' ? cfg.height : DEFAULT_LOOP_H;
        loopNode.style = { width, height };
        loopNode.width = width;
        loopNode.height = height;
        return;
      }

      const minX = Math.min(...children.map((c) => c.position.x));
      const minY = Math.min(...children.map((c) => c.position.y));
      const maxX = Math.max(...children.map((c) => c.position.x + EST_NODE_W));
      const maxY = Math.max(...children.map((c) => c.position.y + EST_NODE_H));

      const origin = { x: minX - LOOP_PAD_X, y: minY - LOOP_PAD_TOP };
      const fitWidth = maxX - minX + LOOP_PAD_X * 2;
      const fitHeight = maxY - minY + LOOP_PAD_TOP + LOOP_PAD_BOTTOM;

      // Tamanho manual tem prioridade; nunca menor que o necessário p/ caber os filhos.
      const width = typeof cfg.width === 'number' ? Math.max(cfg.width, fitWidth) : fitWidth;
      const height = typeof cfg.height === 'number' ? Math.max(cfg.height, fitHeight) : fitHeight;

      loopNode.position = origin;
      loopNode.style = { width, height };
      loopNode.width = width;
      loopNode.height = height;

      children.forEach((child) => {
        child.parentId = loop.id;
        child.extent = 'parent';
        child.position = { x: child.position.x - origin.x, y: child.position.y - origin.y };
        child.zIndex = 10;
        insideIds.add(child.id);
      });
    });

  // React Flow exige que o nó pai apareça antes dos filhos no array.
  const parents = studio.filter((n) => n.type === 'loopGroup');
  const rest = studio.filter((n) => n.type !== 'loopGroup');
  return { nodes: [...parents, ...rest], insideIds, loopBodies };
}

// Aresta "estrutural" do loop = liga o nó Loop ao próprio corpo (entrada ou retorno).
// Essas são escondidas no canvas (o container já comunica o agrupamento) e substituídas
// pelo indicador de repetição decorativo do container — evita linhas cruzando os cards.
function isStructuralLoopEdge(e: FlowEdge, loopBodies: Map<string, Set<string>>): boolean {
  for (const [loopId, body] of loopBodies) {
    if (e.source === loopId && body.has(e.target)) return true; // loop → primeiro filho
    if (e.isLoopBack && e.target === loopId && body.has(e.source)) return true; // filho → loop
  }
  return false;
}

// --- localStorage: rascunho temporário (conveniência de UX, NÃO substitui backend) ---
const draftKey = (id: string) => `agentstudio:draft:${id}`;
function persistDraft(get: () => FlowStoreState) {
  const { flowId } = get();
  if (!flowId) return;
  try {
    localStorage.setItem(draftKey(flowId), JSON.stringify(get().toFlowDTO()));
  } catch {
    /* armazenamento indisponível — ignorar */
  }
}

export const useFlowStore = create<FlowStoreState>((set, get) => ({
  flowId: null,
  meta: null,
  nodes: [],
  edges: [],
  selectedNodeId: null,
  dirty: false,
  runStatus: {},
  pendingDeleteIds: [],

  loadFlow: (flow) => {
    const { nodes, edges, ...meta } = flow;
    const { nodes: studioNodes, insideIds, loopBodies } = buildLoopLayout(nodes, edges);
    const studioEdges = edges.map((e) => {
      const edge = toStudioEdge(e);
      // Esconde as arestas estruturais loop↔corpo (evita o emaranhado de linhas).
      if (isStructuralLoopEdge(e, loopBodies)) edge.hidden = true;
      // Eleva o z-index das arestas internas ao loop p/ ficarem acima do fill do container.
      if (insideIds.has(e.source) || insideIds.has(e.target)) edge.zIndex = 6;
      return edge;
    });
    set({
      flowId: flow.id,
      meta,
      nodes: studioNodes,
      edges: studioEdges,
      selectedNodeId: null,
      dirty: false,
      runStatus: {},
      pendingDeleteIds: [],
    });
  },

  reset: () =>
    set({
      flowId: null,
      meta: null,
      nodes: [],
      edges: [],
      selectedNodeId: null,
      dirty: false,
      runStatus: {},
      pendingDeleteIds: [],
    }),

  onNodesChange: (changes) => {
    set({ nodes: applyNodeChanges(changes, get().nodes) as StudioNode[] });
    if (changes.some((c) => c.type === 'position' || c.type === 'remove')) {
      set({ dirty: true });
      persistDraft(get);
    }
  },

  onEdgesChange: (changes) => {
    set({ edges: applyEdgeChanges(changes, get().edges) as StudioEdge[] });
    if (changes.some((c) => c.type === 'remove')) {
      set({ dirty: true });
      persistDraft(get);
    }
  },

  onConnect: (conn) => {
    if (!conn.source || !conn.target) return;
    // Aresta que volta a um nó já à esquerda → tratada como loopback (visual distinto).
    const nodes = get().nodes;
    const src = nodes.find((n) => n.id === conn.source);
    const tgt = nodes.find((n) => n.id === conn.target);
    const isLoopBack =
      !!src && !!tgt && tgt.position.x <= src.position.x && conn.source !== conn.target;
    const edge: StudioEdge = {
      id: uid('edge'),
      source: conn.source,
      target: conn.target,
      sourceHandle: conn.sourceHandle ?? undefined,
      targetHandle: conn.targetHandle ?? undefined,
      type: isLoopBack ? 'loop' : 'animated',
      label: isLoopBack ? 'loop' : undefined,
      data: { isLoopBack },
    };
    set({ edges: [...get().edges, edge], dirty: true });
    persistDraft(get);
  },

  deleteEdge: (id) => {
    set({ edges: get().edges.filter((e) => e.id !== id), dirty: true });
    persistDraft(get);
  },

  addNode: (kind, position, parentId) => {
    let node: StudioNode;
    if (kind === 'loop') {
      // Loop nasce já como container (igual ao de um template).
      const width = 380;
      const height = 240;
      node = {
        id: uid('node'),
        type: 'loopGroup',
        position,
        width,
        height,
        style: { width, height },
        zIndex: 0,
        data: { kind, config: { ...defaultNodeData(kind), width, height } },
      };
    } else {
      // Se solto dentro de um container de loop, vira filho (posição relativa ao pai).
      const parent = parentId ? get().nodes.find((n) => n.id === parentId) : undefined;
      const isChild = parent?.type === 'loopGroup';
      node = {
        id: uid('node'),
        type: 'studio',
        position: isChild
          ? { x: position.x - parent!.position.x, y: position.y - parent!.position.y }
          : position,
        ...(isChild ? { parentId, extent: 'parent' as const, zIndex: 10 } : {}),
        data: { kind, config: defaultNodeData(kind) },
      };
    }
    set({ nodes: parentsFirst([...get().nodes, node]), selectedNodeId: node.id, dirty: true });
    persistDraft(get);
  },

  reparentNode: (childId, newParentId) => {
    const nodes = get().nodes;
    const child = nodes.find((n) => n.id === childId);
    if (!child || child.type === 'loopGroup') return;
    if ((child.parentId ?? null) === (newParentId ?? null)) return;

    const curParent = child.parentId ? nodes.find((n) => n.id === child.parentId) : undefined;
    const abs = {
      x: (curParent?.position.x ?? 0) + child.position.x,
      y: (curParent?.position.y ?? 0) + child.position.y,
    };
    const newParent = newParentId ? nodes.find((n) => n.id === newParentId) : undefined;
    const rel = newParent ? { x: abs.x - newParent.position.x, y: abs.y - newParent.position.y } : abs;

    const updated = nodes.map((n) =>
      n.id === childId
        ? {
            ...n,
            parentId: newParentId ?? undefined,
            extent: newParentId ? ('parent' as const) : undefined,
            zIndex: newParentId ? 10 : undefined,
            position: rel,
          }
        : n,
    );
    set({ nodes: parentsFirst(updated), dirty: true });
    persistDraft(get);
  },

  updateNodeConfig: (id, patch) => {
    set({
      nodes: get().nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, config: { ...n.data.config, ...patch } } } : n,
      ),
      dirty: true,
    });
    persistDraft(get);
  },

  setLoopSize: (id, width, height) => {
    set({
      nodes: get().nodes.map((n) =>
        n.id === id
          ? {
              ...n,
              width,
              height,
              style: { ...n.style, width, height },
              data: { ...n.data, config: { ...n.data.config, width, height } },
            }
          : n,
      ),
      dirty: true,
    });
    persistDraft(get);
  },

  setNodeOversight: (id, mode) => {
    set({
      nodes: get().nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, oversightOverride: mode } } : n,
      ),
      dirty: true,
    });
    persistDraft(get);
  },

  requestDeleteNodes: (ids) => set({ pendingDeleteIds: ids.filter(Boolean) }),
  cancelDelete: () => set({ pendingDeleteIds: [] }),

  deleteNode: (id) => {
    const target = get().nodes.find((n) => n.id === id);
    let nodes = get().nodes.filter((n) => n.id !== id);
    // Ao excluir um container de loop, libera os filhos (converte p/ posição absoluta).
    if (target?.type === 'loopGroup') {
      nodes = nodes.map((n) =>
        n.parentId === id
          ? {
              ...n,
              parentId: undefined,
              extent: undefined,
              zIndex: undefined,
              position: { x: target.position.x + n.position.x, y: target.position.y + n.position.y },
            }
          : n,
      );
    }
    set({
      nodes,
      edges: get().edges.filter((e) => e.source !== id && e.target !== id),
      selectedNodeId: get().selectedNodeId === id ? null : get().selectedNodeId,
      dirty: true,
    });
    persistDraft(get);
  },

  duplicateNode: (id) => {
    const node = get().nodes.find((n) => n.id === id);
    if (!node) return;
    const copy: StudioNode = {
      ...node,
      id: uid('node'),
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: false,
      data: { ...node.data, config: { ...node.data.config } },
    };
    set({ nodes: [...get().nodes, copy], selectedNodeId: copy.id, dirty: true });
    persistDraft(get);
  },

  setSelected: (id) => set({ selectedNodeId: id }),

  updateMeta: (patch) => {
    const meta = get().meta;
    if (!meta) return;
    set({ meta: { ...meta, ...patch }, dirty: true });
    persistDraft(get);
  },

  setOversightMode: (mode) => {
    const meta = get().meta;
    if (!meta) return;
    set({ meta: { ...meta, oversightMode: mode }, dirty: true });
    persistDraft(get);
  },

  toFlowDTO: () => {
    const { meta, nodes, edges } = get();
    const byId = new Map(nodes.map((n) => [n.id, n]));
    // Converte posições de filhos de loop (relativas ao container) de volta para absolutas.
    const dtoNodes = nodes.map((n) => {
      const dto = fromStudioNode(n);
      if (n.parentId) {
        const parent = byId.get(n.parentId);
        if (parent) {
          dto.position = {
            x: parent.position.x + n.position.x,
            y: parent.position.y + n.position.y,
          };
        }
      }
      return dto;
    });
    return {
      ...(meta as Omit<Flow, 'nodes' | 'edges'>),
      nodes: dtoNodes,
      edges: edges.map(fromStudioEdge),
    } as Flow;
  },

  markSaved: () => set({ dirty: false }),

  setNodeRunStatus: (nodeId, state) =>
    set((s) => ({ runStatus: { ...s.runStatus, [nodeId]: state } })),

  resetRunStatus: () => set({ runStatus: {} }),
}));
